import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useToast } from '@shared/hooks/use-toast';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { PackingStationsApiAdapter } from '../adapters/PackingStationsApiAdapter';
import { Service } from '../services/Service';
import { getApiErrorMessage } from '../utils/getApiErrorMessage';

export const useCreatePackingStation = () => {
  const { t } = useTranslation('packingStationsModule');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new PackingStationsApiAdapter(httpClient);
  const service = new Service(apiAdapter);

  const invalidatePackingStationsList = () => {
    void queryClient.invalidateQueries({ queryKey: ['packing-stations'] });
  };

  const createBareStation = async (params: { code: string; active: boolean }): Promise<PackingStation | null> => {
    setIsCreating(true);
    try {
      const station = await service.create({
        code: params.code,
        active: params.active,
      });
      toast({
        title: t('toasts.create.success.title'),
        description: t('toasts.create.success.description'),
        variant: 'default',
      });
      invalidatePackingStationsList();
      return station;
    } catch (error: unknown) {
      toast({
        title: t('toasts.create.error.title'),
        description: getApiErrorMessage(error, t('toasts.create.error.description')),
        variant: 'destructive',
      });
      return null;
    } finally {
      setIsCreating(false);
    }
  };

  const updateStation = async (
    id: number,
    next: { code: string; active: boolean },
    previous: { code: string; active: boolean },
  ): Promise<boolean> => {
    const trimmed = next.code.trim();
    if (!trimmed) {
      toast({
        title: t('toasts.updateDetail.validation.title'),
        description: t('toasts.updateDetail.validation.emptyCode'),
        variant: 'destructive',
      });
      return false;
    }

    const codeChanged = trimmed !== previous.code.trim();
    const activeChanged = next.active !== previous.active;

    if (!codeChanged && !activeChanged) {
      return true;
    }

    setIsUpdating(true);
    try {
      if (codeChanged) {
        await service.updatePackingStation(id, { code: trimmed });
      }
      if (activeChanged) {
        await service.toggleStatus(id, next.active);
      }

      toast({
        title: t('toasts.updateDetail.success.title'),
        description: t('toasts.updateDetail.success.description'),
        variant: 'default',
      });

      invalidatePackingStationsList();
      void queryClient.invalidateQueries({ queryKey: ['packing-station-detail', id] });
      return true;
    } catch (error: unknown) {
      toast({
        title: t('toasts.updateDetail.error.title'),
        description: getApiErrorMessage(error, t('toasts.updateDetail.error.description')),
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    createBareStation,
    updateStation,
    isCreating,
    isUpdating,
  };
};
